import { CircleCheck, PartyPopper } from "lucide-react";
import { useTranslate } from "ra-core";
import { Skeleton } from "@/components/ui/skeleton";
import { useGavionConfig } from "../config/useGavionConfig";
import { GOAL_KEYS, goalProgress, resolveGoals } from "../domain/goals";
import { useReportsClient } from "../reports/ReportsClientContext";
import { useReport } from "../reports/useReport";
import { leaderboardKey } from "./leaderboardKey";

const POP =
  "motion-safe:animate-in motion-safe:zoom-in-75 motion-safe:fade-in motion-safe:duration-500";

/**
 * "Ma journée" (X1): progress toward today's targets. Counts come from gavion_leaderboard('today') (the caller's own
 * row; none yet = zeros), targets from configuration.config.gavion.goals (defaults, then the rep's override).
 */
export const MyDayGoals = ({
  salesId,
  now,
}: {
  salesId: number;
  now: Date;
}) => {
  const translate = useTranslate();
  const client = useReportsClient();
  const { config, isPending: configPending } = useGavionConfig();
  const q = useReport(leaderboardKey("today", now), () =>
    client.leaderboard("today"),
  );
  const targets = resolveGoals(config.goals, salesId);
  const mine = q.data?.rows.find((r) => r.sales_id === salesId);
  const goals = GOAL_KEYS.filter((k) => targets[k] > 0).map((k) => {
    const count = mine?.[k] ?? 0;
    return {
      key: k,
      count,
      target: targets[k],
      ...goalProgress(count, targets[k]),
    };
  });
  const allHit = goals.length > 0 && goals.every((g) => g.hit);

  const body = () => {
    if (q.isPending || configPending)
      return <Skeleton className="h-24 w-full rounded-lg" />;
    if (q.isError)
      return (
        <p role="alert" className="text-sm text-destructive">
          {translate("gavion.goals.my_day.error")}
        </p>
      );
    if (goals.length === 0)
      return (
        <p className="text-sm text-muted-foreground">
          {translate("gavion.goals.my_day.no_goals")}
        </p>
      );
    return (
      <>
        <ul className="grid gap-4 sm:grid-cols-3">
          {goals.map((g) => {
            const label = translate(`gavion.goals.metrics.${g.key}`);
            const valueText = translate("gavion.goals.my_day.progress", {
              count: g.count,
              target: g.target,
            });
            return (
              <li key={g.key} className="min-w-0 space-y-1.5">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold">{label}</span>
                  <span className="text-sm tabular-nums text-muted-foreground">
                    <span className="text-base font-extrabold text-foreground">
                      {g.count}
                    </span>{" "}
                    / {g.target}
                  </span>
                </div>
                <div
                  role="progressbar"
                  aria-label={label}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={g.percent}
                  aria-valuetext={valueText}
                  className="h-2.5 w-full overflow-hidden rounded-full bg-primary/15"
                >
                  <div
                    className="h-full rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-700"
                    style={{ width: `${g.percent}%` }}
                  />
                </div>
                {g.hit ? (
                  <p
                    className={`flex items-center gap-1 text-xs font-bold text-primary ${POP}`}
                  >
                    <CircleCheck className="size-3.5" aria-hidden="true" />
                    {translate("gavion.goals.my_day.hit")}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {translate("gavion.goals.my_day.remaining", {
                      smart_count: g.target - g.count,
                    })}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
        {allHit && (
          <p
            role="status"
            className={`mt-4 flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-2 text-sm font-semibold text-foreground ${POP}`}
          >
            <PartyPopper
              className="size-4 shrink-0 text-primary"
              aria-hidden="true"
            />
            {translate("gavion.goals.my_day.all_hit")}
          </p>
        )}
      </>
    );
  };

  return (
    <section
      aria-labelledby="gavion-my-day"
      className="rounded-xl border border-t-4 border-t-primary bg-card p-4"
    >
      <div className="mb-3">
        <h2 id="gavion-my-day" className="text-lg font-bold">
          {translate("gavion.goals.my_day.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("gavion.goals.my_day.subtitle")}
        </p>
      </div>
      {body()}
    </section>
  );
};
